import { MastershopSyncStatus } from '../gamification/MastershopSyncStatus';
import React, { useState, useEffect } from 'react';
import { 
  Key, 
  CheckCircle2, 
  AlertCircle, 
  RefreshCw, 
  Trash2, 
  Plus, 
  Store, 
  ShieldCheck, 
  ShoppingCart, 
  HelpCircle, 
  Eye, 
  EyeOff, 
  Sparkles, 
  Clock, 
  TrendingUp,
  Layers,
  ChevronRight,
  Edit3
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { mastershopHelpers, getCurrentYearMonth, formatMonthName } from '../../helpers/mastershopHelpers';
import { SellerStoreIntegration, SellerRevenueMonthly } from '../../types/sellerIntegration';
import { useToast } from '../../contexts/ToastContext';

interface StoreIntegrationsTabProps {
  userId: string;
}

export const StoreIntegrationsTab: React.FC<StoreIntegrationsTabProps> = ({ userId }) => {
  const currentMonth = getCurrentYearMonth();
  const currentMonthLabel = formatMonthName(currentMonth);
  const { showToast } = useToast();

  const [stores, setStores] = useState<SellerStoreIntegration[]>([]);
  const [revenue, setRevenue] = useState<SellerRevenueMonthly | null>(null);
  const [loading, setLoading] = useState(true);

  // Formulário de Nova Loja
  const [isAddingStore, setIsAddingStore] = useState(false);
  const [newStoreName, setNewStoreName] = useState('');
  const [newApiKey, setNewApiKey] = useState('');
  const [showNewKey, setShowNewKey] = useState(false);
  const [savingNew, setSavingNew] = useState(false);

  // Formulário de Edição
  const [editingStoreId, setEditingStoreId] = useState<string | null>(null);
  const [editStoreName, setEditStoreName] = useState('');
  const [editApiKey, setEditApiKey] = useState('');
  const [showEditKey, setShowEditKey] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  // Estados de Sincronização e Exclusão
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingStoreId, setSyncingStoreId] = useState<string | null>(null);
  const [deletingStoreId, setDeletingStoreId] = useState<string | null>(null);

  useEffect(() => {
    if (userId) {
      loadAllData();
    }
  }, [userId]);

  const loadAllData = async () => {
    try {
      setLoading(true);
      const [storesRes, revRes] = await Promise.all([
        mastershopHelpers.getUserStores(userId),
        mastershopHelpers.getMonthlyRevenue(userId, currentMonth),
      ]);

      setStores(storesRes.data || []);
      if (revRes.data) {
        setRevenue(revRes.data);
      }
    } catch (err) {
      console.error('Erro ao carregar lojas integradas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewStore = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanKey = newApiKey.trim();
    if (!cleanKey) {
      showToast({
        type: 'warning',
        title: 'Chave Obrigatória',
        message: 'Por favor, insira a sua ApiKey da Master Shop.',
      });
      return;
    }

    const defaultName = newStoreName.trim() || `Loja ${stores.length + 1}`;

    try {
      setSavingNew(true);
      // 1. Salvar loja no banco de dados
      const saveRes = await mastershopHelpers.saveApiKey(userId, cleanKey, defaultName);
      if (!saveRes.success) {
        throw new Error(saveRes.error || 'Erro ao cadastrar loja.');
      }

      showToast({
        type: 'success',
        title: 'Loja Cadastrada! 🎉',
        message: `Sincronizando ${defaultName} com a Master Shop...`,
      });

      // Resetar formulário
      setNewApiKey('');
      setNewStoreName('');
      setIsAddingStore(false);

      // 2. Disparar sincronização consolidada
      await mastershopHelpers.syncRevenue(userId, currentMonth);

      showToast({
        type: 'success',
        title: 'Loja Conectada com Sucesso!',
        message: `O faturamento de ${defaultName} já foi adicionado ao seu total mensal.`,
      });

      await loadAllData();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro ao Conectar Loja',
        message: err.message || 'Verifique a chave informada e tente novamente.',
      });
    } finally {
      setSavingNew(false);
    }
  };

  const handleStartEdit = (store: SellerStoreIntegration) => {
    setEditingStoreId(store.id);
    setEditStoreName(store.store_name || '');
    setEditApiKey(store.api_key || '');
    setShowEditKey(false);
    setIsAddingStore(false);
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingStoreId) return;

    const cleanKey = editApiKey.trim();
    if (!cleanKey) {
      showToast({
        type: 'warning',
        title: 'Chave Obrigatória',
        message: 'A chave de API não pode ficar vazia.',
      });
      return;
    }

    const name = editStoreName.trim() || 'Loja Master';

    try {
      setSavingEdit(true);
      const saveRes = await mastershopHelpers.saveApiKey(userId, cleanKey, name, editingStoreId);
      if (!saveRes.success) {
        throw new Error(saveRes.error || 'Falha ao atualizar loja.');
      }

      showToast({
        type: 'success',
        title: 'Loja Atualizada!',
        message: `Os dados de "${name}" foram salvos com sucesso.`,
      });

      setEditingStoreId(null);
      // Sincronizar após editar
      await mastershopHelpers.syncRevenue(userId, currentMonth);
      await loadAllData();
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro ao Salvar',
        message: err.message || 'Não foi possível atualizar a loja.',
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSyncAll = async () => {
    try {
      setSyncingAll(true);
      const res = await mastershopHelpers.syncRevenue(userId, currentMonth);
      if (res.success) {
        showToast({
          type: 'success',
          title: 'Sincronização solicitada',
          message: res.data?.queued === false ? 'Já houve uma solicitação recente. Aguarde cinco minutos para solicitar outra.' : 'Processamento em segundo plano. O total será atualizado ao concluir todas as lojas.',
        });
        await loadAllData();
      } else {
        showToast({
          type: 'error',
          title: 'Erro na Sincronização',
          message: res.error || 'Não foi possível sincronizar suas lojas.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message || 'Falha ao sincronizar.',
      });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSyncSingleStore = async (store: SellerStoreIntegration) => {
    try {
      setSyncingStoreId(store.id);
      const res = await mastershopHelpers.syncRevenue(userId, currentMonth, store.api_key);
      if (res.success) {
        showToast({
          type: 'success',
          title: 'Sincronização solicitada',
          message: res.data?.queued === false ? 'Já houve uma solicitação recente. Aguarde cinco minutos para solicitar outra.' : 'Processamento em segundo plano. O total será atualizado ao concluir todas as lojas.',
        });
        await loadAllData();
      } else {
        showToast({
          type: 'error',
          title: 'Falha na Sincronização',
          message: res.error || 'Verifique a chave de API.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message || 'Erro ao sincronizar.',
      });
    } finally {
      setSyncingStoreId(null);
    }
  };

  const handleDeleteStore = async (store: SellerStoreIntegration) => {
    const storeTitle = store.store_name || 'esta loja';
    if (!window.confirm(`Tem certeza que deseja desconectar e remover "${storeTitle}"?`)) {
      return;
    }

    try {
      setDeletingStoreId(store.id);
      const res = await mastershopHelpers.deleteStore(store.id, userId);
      if (res.success) {
        showToast({
          type: 'info',
          title: 'Loja Removida',
          message: `A integração com "${storeTitle}" foi desconectada.`,
        });
        // Re-sincronizar faturamento consolidado restante
        await mastershopHelpers.syncRevenue(userId, currentMonth);
        await loadAllData();
      } else {
        showToast({
          type: 'error',
          title: 'Erro ao Remover',
          message: res.error || 'Não foi possível remover a loja.',
        });
      }
    } catch (err: any) {
      showToast({
        type: 'error',
        title: 'Erro',
        message: err.message,
      });
    } finally {
      setDeletingStoreId(null);
    }
  };

  const formatBrl = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      maximumFractionDigits: 2,
    }).format(val || 0);
  };

  const formatCop = (val: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(val || 0);
  };

  const maskApiKey = (key: string) => {
    if (!key) return '••••••••';
    if (key.length <= 8) return '••••••••';
    return `${key.slice(0, 7)}...${key.slice(-4)}`;
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-gray-400 space-y-3">
        <RefreshCw className="w-8 h-8 animate-spin text-[#0FFF55] mx-auto" />
        <p className="text-sm font-medium">Carregando suas lojas e integrações...</p>
      </div>
    );
  }

  const totalConsolidatedBrl = revenue?.total_revenue ?? 0;
  const totalConsolidatedCop = revenue?.revenue_cop ?? 0;
  const totalOrders = revenue?.total_orders ?? 0;

  return (
    <div className="space-y-8">
      <MastershopSyncStatus month={currentMonth} userId={userId} onUpdate={loadAllData} />
      {/* Header da Seção de Integrações */}
      <div className="bg-[#1a1d23]/80 backdrop-blur-md rounded-3xl border border-gray-700/60 p-6 sm:p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0FFF55]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pb-6 border-b border-gray-800">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-tr from-[#0dd149] to-[#0FFF55] rounded-2xl flex items-center justify-center text-[#121316] font-black text-xl shadow-lg shadow-[#0FFF55]/20 flex-shrink-0">
              MS
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-2xl font-black text-white tracking-tight">Master Shop</h2>
                {stores.length > 0 ? (
                  <span className="bg-[#0FFF55]/20 text-[#0FFF55] border border-[#0FFF55]/40 text-xs font-bold px-2.5 py-0.5 rounded-full flex items-center gap-1.5">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {stores.length === 1 ? '1 Loja Conectada' : `${stores.length} Lojas Conectadas`}
                  </span>
                ) : (
                  <span className="bg-gray-700/40 text-gray-400 text-xs font-medium px-2.5 py-0.5 rounded-full">
                    Nenhuma loja conectada
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-xs sm:text-sm mt-1">
                Conecte uma ou mais lojas da Master Shop. O faturamento de todas as suas lojas é somado automaticamente para a régua de metas.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 self-start sm:self-auto">
            {stores.length > 0 && (
              <Button
                onClick={handleSyncAll}
                disabled={syncingAll}
                size="sm"
                className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] hover:from-[#0dd149] hover:to-[#0FFF55] text-[#121316] font-black rounded-xl text-xs flex items-center gap-1.5 shadow-md shadow-[#0FFF55]/20 transition-transform active:scale-95"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${syncingAll ? 'animate-spin' : ''}`} />
                {syncingAll ? 'Sincronizando Todas...' : 'Sincronizar Todas'}
              </Button>
            )}
          </div>
        </div>

        {/* Resumo Consolidado Multi-Lojas */}
        {stores.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6">
            <div className="bg-[#14161b] p-4 rounded-2xl border border-gray-800">
              <span className="text-xs text-gray-400 block font-medium">Faturamento Consolidado • {currentMonthLabel}</span>
              <span className="text-2xl font-black text-[#0FFF55] block mt-0.5">
                {formatBrl(totalConsolidatedBrl)}
              </span>
              {totalConsolidatedCop > 0 && (
                <span className="text-[11px] text-gray-500 block">
                  Original: {formatCop(totalConsolidatedCop)}
                </span>
              )}
            </div>

            <div className="bg-[#14161b] p-4 rounded-2xl border border-gray-800">
              <span className="text-xs text-gray-400 block font-medium">Total de Pedidos no Mês</span>
              <span className="text-2xl font-black text-white block mt-0.5">
                {totalOrders} pedidos
              </span>
              <span className="text-[11px] text-gray-500 block">Soma de todas as lojas</span>
            </div>

            <div className="bg-[#14161b] p-4 rounded-2xl border border-gray-800">
              <span className="text-xs text-gray-400 block font-medium">Meta Atual da Régua</span>
              <span className="text-2xl font-black text-white block mt-0.5">
                {formatBrl(revenue?.current_milestone_target || 100000)}
              </span>
              <span className="text-[11px] text-[#0FFF55] font-semibold block">
                {totalConsolidatedBrl >= (revenue?.current_milestone_target || 100000) ? '🎯 Meta Conquistada!' : 'Em progresso'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Formulário para Adicionar / Conectar Nova Loja */}
      {isAddingStore && (
        <div className="bg-[#191b22] rounded-3xl border border-[#0FFF55]/40 p-6 sm:p-7 shadow-2xl animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-800">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-[#0FFF55]/10 border border-[#0FFF55]/20 rounded-xl text-[#0FFF55]">
                <Plus className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-white">Conectar Nova Loja da Master Shop</h3>
                <p className="text-xs text-gray-400">Insira um nome para identificar a loja e a respectiva ApiKey.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsAddingStore(false)}
              className="text-gray-400 hover:text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Cancelar
            </button>
          </div>

          <form onSubmit={handleAddNewStore} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Nome da Loja */}
              <div className="space-y-1.5">
                <Label htmlFor="store-name" className="text-gray-300 font-semibold text-xs flex items-center gap-1.5">
                  <Store className="w-3.5 h-3.5 text-[#0FFF55]" />
                  Nome da Loja
                </Label>
                <Input
                  id="store-name"
                  type="text"
                  value={newStoreName}
                  onChange={(e) => setNewStoreName(e.target.value)}
                  placeholder={`Ex: Loja Cosméticos, Loja ${stores.length + 1}`}
                  className="bg-[#121418] border-gray-700 text-white placeholder-gray-500 rounded-xl px-3.5 h-10 focus:ring-1 focus:ring-[#0FFF55] focus:border-[#0FFF55] text-xs"
                />
              </div>

              {/* Chave de API */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="store-api-key" className="text-gray-300 font-semibold text-xs flex items-center gap-1.5">
                    <Key className="w-3.5 h-3.5 text-[#0FFF55]" />
                    Chave de API (ApiKey)
                  </Label>
                  <span className="text-[10px] text-gray-500 font-mono">ms_live_...</span>
                </div>
                <div className="relative">
                  <Input
                    id="store-api-key"
                    type={showNewKey ? 'text' : 'password'}
                    value={newApiKey}
                    onChange={(e) => setNewApiKey(e.target.value)}
                    placeholder="Cole a ApiKey da Master Shop"
                    className="bg-[#121418] border-gray-700 text-white placeholder-gray-500 rounded-xl px-3.5 pr-10 h-10 focus:ring-1 focus:ring-[#0FFF55] focus:border-[#0FFF55] text-xs font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewKey(!showNewKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-1"
                  >
                    {showNewKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3 border-t border-gray-800">
              <div className="flex items-center gap-2 text-[11px] text-gray-400">
                <ShieldCheck className="w-3.5 h-3.5 text-[#0FFF55]" />
                Armazenada com segurança no banco de dados.
              </div>

              <div className="flex items-center justify-end gap-2.5">
                <Button
                  type="button"
                  onClick={() => setIsAddingStore(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white text-xs h-9 px-3 rounded-xl"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={savingNew}
                  size="sm"
                  className="bg-[#0FFF55] hover:bg-[#0dd149] text-[#121316] font-black px-5 h-9 rounded-xl shadow-md shadow-[#0FFF55]/20 flex items-center gap-1.5 text-xs"
                >
                  {savingNew ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      Conectando...
                    </>
                  ) : (
                    <>
                      <Plus className="w-3.5 h-3.5" />
                      Salvar e Conectar Loja
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Formulário para Editar Loja Existente */}
      {editingStoreId && (
        <div className="bg-[#191b22] rounded-3xl border border-[#0FFF55]/40 p-6 sm:p-7 shadow-2xl animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="flex items-center justify-between pb-4 mb-5 border-b border-gray-800">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-[#0FFF55]/10 border border-[#0FFF55]/20 rounded-xl text-[#0FFF55]">
                <Edit3 className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-bold text-white">Editar Dados / Trocar Chave da Loja</h3>
                <p className="text-xs text-gray-400">Atualize o nome de exibição ou substitua a ApiKey desta loja.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setEditingStoreId(null)}
              className="text-gray-400 hover:text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Cancelar
            </button>
          </div>

          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-gray-300 font-semibold text-xs flex items-center gap-1.5">
                  <Store className="w-3.5 h-3.5 text-[#0FFF55]" />
                  Nome da Loja
                </Label>
                <Input
                  type="text"
                  value={editStoreName}
                  onChange={(e) => setEditStoreName(e.target.value)}
                  placeholder="Nome da loja"
                  className="bg-[#121418] border-gray-700 text-white placeholder-gray-500 rounded-xl px-3.5 h-10 focus:ring-1 focus:ring-[#0FFF55] focus:border-[#0FFF55] text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-gray-300 font-semibold text-xs flex items-center gap-1.5">
                    <Key className="w-3.5 h-3.5 text-[#0FFF55]" />
                    Chave de API (ApiKey)
                  </Label>
                  <span className="text-[10px] text-gray-500 font-mono">ms_live_...</span>
                </div>
                <div className="relative">
                  <Input
                    type={showEditKey ? 'text' : 'password'}
                    value={editApiKey}
                    onChange={(e) => setEditApiKey(e.target.value)}
                    placeholder="Cole a nova ApiKey da Master Shop"
                    className="bg-[#121418] border-gray-700 text-white placeholder-gray-500 rounded-xl px-3.5 pr-10 h-10 focus:ring-1 focus:ring-[#0FFF55] focus:border-[#0FFF55] text-xs font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowEditKey(!showEditKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white p-1"
                  >
                    {showEditKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-gray-800">
              <Button
                type="button"
                onClick={() => setEditingStoreId(null)}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white text-xs h-9 px-3 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={savingEdit}
                size="sm"
                className="bg-[#0FFF55] hover:bg-[#0dd149] text-[#121316] font-black px-5 h-9 rounded-xl shadow-md shadow-[#0FFF55]/20 flex items-center gap-1.5 text-xs"
              >
                {savingEdit ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    Salvar Alterações
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Lojas Conectadas */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Store className="w-5 h-5 text-[#0FFF55]" />
            <h3 className="text-lg font-bold text-white">Suas Lojas Conectadas ({stores.length})</h3>
          </div>

          {stores.length > 0 && !isAddingStore && (
            <Button
              onClick={() => {
                setIsAddingStore(true);
                setEditingStoreId(null);
              }}
              size="sm"
              className="bg-[#0FFF55] text-[#121316] hover:bg-[#0dd149] font-black rounded-xl text-xs flex items-center gap-1.5 shadow-md shadow-[#0FFF55]/20"
            >
              <Plus className="w-4 h-4" />
              Adicionar Nova Loja
            </Button>
          )}
        </div>

        {stores.length === 0 ? (
          <div className="bg-[#1a1d23]/40 border border-dashed border-gray-700/80 rounded-3xl p-12 text-center space-y-4">
            <div className="w-16 h-16 bg-gray-800/80 text-gray-400 rounded-2xl flex items-center justify-center mx-auto">
              <Store className="w-8 h-8 text-[#0FFF55]" />
            </div>
            <div className="space-y-1">
              <h4 className="text-base font-bold text-white">Você ainda não tem lojas conectadas</h4>
              <p className="text-xs text-gray-400 max-w-md mx-auto">
                Conecte a chave de API da sua loja para que suas vendas comecem a contabilizar na régua de metas e no ranking do Master Club.
              </p>
            </div>
            <Button
              onClick={() => setIsAddingStore(true)}
              className="bg-gradient-to-r from-[#0FFF55] to-[#0dd149] text-[#121316] font-black px-6 py-2 rounded-xl text-xs shadow-lg shadow-[#0FFF55]/20"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              Conectar Loja Agora
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stores.map((store, index) => {
              const isSyncingThis = syncingStoreId === store.id;
              const isDeletingThis = deletingStoreId === store.id;
              const hasError = store.last_sync_status === 'error';
              const isSuccess = store.last_sync_status === 'success';

              return (
                <div
                  key={store.id}
                  className={`bg-gradient-to-br from-[#1a1d23] to-[#14161b] rounded-2xl border p-5 shadow-xl space-y-4 transition-all ${
                    hasError ? 'border-red-500/40 bg-red-950/10' : 'border-gray-800 hover:border-gray-700'
                  }`}
                >
                  {/* Top: Nome, Tag e Ações */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-[#0FFF55]/10 border border-[#0FFF55]/20 flex items-center justify-center text-[#0FFF55] font-black text-sm flex-shrink-0">
                        #{index + 1}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-extrabold text-white text-base truncate">
                          {store.store_name || `Loja ${index + 1}`}
                        </h4>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {isSuccess ? (
                            <span className="text-[11px] font-bold text-green-400 flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Ativa e Sincronizada
                            </span>
                          ) : hasError ? (
                            <span className="text-[11px] font-bold text-red-400 flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" /> Erro na ApiKey
                            </span>
                          ) : (
                            <span className="text-[11px] font-bold text-yellow-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Sincronização pendente
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button
                        onClick={() => handleSyncSingleStore(store)}
                        disabled={isSyncingThis || syncingAll}
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-gray-300 hover:text-white hover:bg-gray-800 rounded-xl text-xs h-8 px-2.5"
                        title="Sincronizar esta loja"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${isSyncingThis ? 'animate-spin text-[#0FFF55]' : ''}`} />
                      </Button>
                      <Button
                        onClick={() => handleStartEdit(store)}
                        variant="outline"
                        size="sm"
                        className="border-gray-700 text-gray-300 hover:text-[#0FFF55] hover:bg-[#0FFF55]/10 rounded-xl text-xs h-8 px-2.5"
                        title="Editar nome ou trocar chave"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        onClick={() => handleDeleteStore(store)}
                        disabled={isDeletingThis}
                        variant="outline"
                        size="sm"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl text-xs h-8 px-2.5"
                        title="Desconectar loja"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {/* Informações de Faturamento e Chave */}
                  <div className="grid grid-cols-2 gap-3 bg-[#111317] p-3 rounded-xl border border-gray-800/80">
                    <div>
                      <span className="text-[10px] text-gray-400 uppercase font-bold block">Faturamento ({currentMonthLabel})</span>
                      <span className="text-base font-black text-[#0FFF55] block mt-0.5">
                        {formatBrl(store.last_sync_revenue_brl || 0)}
                      </span>
                      {Number(store.last_sync_revenue_cop || 0) > 0 && (
                        <span className="text-[10px] text-gray-500 block">
                          {formatCop(store.last_sync_revenue_cop || 0)}
                        </span>
                      )}
                    </div>
                    <div>
                      <span className="text-[10px] text-gray-400 uppercase font-bold block">Pedidos</span>
                      <span className="text-base font-black text-white block mt-0.5">
                        {store.last_sync_orders || 0}
                      </span>
                      <span className="text-[10px] text-gray-500 block">processados</span>
                    </div>
                  </div>

                  {/* Chave Mascarada & Último Sync */}
                  <div className="flex items-center justify-between text-xs text-gray-400 pt-1 border-t border-gray-800/60 font-mono">
                    <span className="flex items-center gap-1.5">
                      <Key className="w-3.5 h-3.5 text-gray-500" />
                      {maskApiKey(store.api_key)}
                    </span>
                    <span className="text-[10px] text-gray-500 font-sans">
                      {store.last_sync_at ? new Date(store.last_sync_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : 'Nunca'}
                    </span>
                  </div>

                  {/* Mensagem de Erro se houver */}
                  {hasError && store.last_error_message && (
                    <p className="text-[11px] text-red-400 bg-red-950/30 p-2 rounded-lg border border-red-900/50 flex items-start gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>{store.last_error_message}</span>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Tutorial Passo a Passo para Gerar a Chave */}
      <div className="bg-[#1a1d23]/50 backdrop-blur-sm rounded-3xl border border-gray-700/50 p-6 sm:p-8 shadow-2xl space-y-4">
        <div className="flex items-center gap-2.5 text-white font-bold text-lg">
          <HelpCircle className="w-5 h-5 text-[#0FFF55]" />
          Como obter a ApiKey na Master Shop?
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
          <div className="bg-gray-800/40 border border-gray-700/40 rounded-2xl p-4 space-y-2">
            <div className="w-7 h-7 rounded-full bg-[#0FFF55]/20 text-[#0FFF55] font-black text-xs flex items-center justify-center">
              1
            </div>
            <h4 className="font-bold text-sm text-white">Acesse sua Conta</h4>
            <p className="text-xs text-gray-400 leading-relaxed">
              Faça login no painel da sua loja na <strong className="text-gray-300">Master Shop</strong>.
            </p>
          </div>

          <div className="bg-gray-800/40 border border-gray-700/40 rounded-2xl p-4 space-y-2">
            <div className="w-7 h-7 rounded-full bg-[#0FFF55]/20 text-[#0FFF55] font-black text-xs flex items-center justify-center">
              2
            </div>
            <h4 className="font-bold text-sm text-white">Configurações de API</h4>
            <p className="text-xs text-gray-400 leading-relaxed">
              Navegue até o menu de <strong className="text-gray-300">Configurações ➔ Integrações / ApiKey</strong> da sua loja.
            </p>
          </div>

          <div className="bg-gray-800/40 border border-gray-700/40 rounded-2xl p-4 space-y-2">
            <div className="w-7 h-7 rounded-full bg-[#0FFF55]/20 text-[#0FFF55] font-black text-xs flex items-center justify-center">
              3
            </div>
            <h4 className="font-bold text-sm text-white">Adicione no Master Club</h4>
            <p className="text-xs text-gray-400 leading-relaxed">
              Copie a sua <strong className="text-gray-300">ApiKey</strong> e cole clicando em "+ Conectar Nova Loja" acima.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
